/**
 * What a client's sheets read to decide how to render (`SheetRegistries.render`).
 * Both reads are made fresh at each decision, so a provider that changes
 * takes effect on the next bind.
 */
export interface RenderFlags {
  /**
   * Whether the SDK 2.0 pipeline feeds the live render path (scene commit,
   * cell directives, the LR6 render skip).
   */
  live(): boolean;
  /**
   * Whether this is a measurement session. Gates verbose, non-free
   * instrumentation only — never render behavior.
   */
  diagnostics(): boolean;
}

/**
 * `RenderFlags` — the switch for the SDK 2.0 render pipeline a client's
 * sheets read (`SheetRegistries.render`), plus the diagnostics switch.
 *
 * ## One flag, three slices
 *
 * The pipeline is three cooperating slices — scene commit (structure),
 * cell directives (per-cell honor), and the LR6 render skip. They were three
 * separate `false` consts (`SCENE_COMMIT_LIVE` / `CELL_DIRECTIVES_LIVE` /
 * `LR6_SKIP_ENABLED`) that were only ever flipped together, so they are now
 * one: `SDK20_LIVE` below. Every decision site reads its sheet's
 * `registries.render.live()`, whose shipped default is that const, so
 * promoting or demoting the whole pipeline is a **one-line** change here.
 *
 * ## The override is two-way
 *
 * `SDK20_LIVE` is the shipped default; a host's persisted render flag
 * supplies a per-device override that wins in **both**
 * directions — a session can opt out of a default-on pipeline, not just into a
 * default-off one. The app seeds that flag's own default from `defaultLive()`,
 * so this const stays the single source of truth: a device that has never
 * touched the toggle simply follows whatever shipped.
 *
 * ## Live vs diagnostics — deliberately separate
 *
 * `live()` answers "does the SDK 2.0 path feed the render?"; `diagnostics()`
 * answers "is this a measurement session?". While the pipeline shipped dark
 * these were the same boolean, harmlessly: the only way to get SDK 2.0 was to
 * turn the debug override on, so "override on" and "measuring" coincided.
 *
 * Once `SDK20_LIVE` is `true` that coincidence breaks and conflating them would
 * be a real regression — the diagnostics gates guard non-free work (an O(n·m)
 * commit-op assembly, per-column breadcrumbs, and the `decideOnce` memo bypass),
 * so every ordinary session would pay a per-bind instrumentation tax and emit a
 * breadcrumb firehose. Diagnostics therefore hang off their own opt-in switch.
 *
 * The dark-path referees run in every configuration, so a divergence is still
 * logged loudly whichever way these read.
 *
 * ## Per client, not per process
 *
 * The flags are a client's: `ClientConfig.registries` carries a `RenderFlags`
 * (`SheetRegistries.render`), every sheet the client opens reads it through
 * `sheet.registries.render`, and two clients in one process render
 * independently. This module holds **no state** — no `window`, no
 * `localStorage`, no DOM, no module-level `let`. It lives in the no-DOM
 * `sheets` package so both `sheets` (`ledger-render-skip.ts`) and `sheets-ui`
 * (`default-binder.ts`) can read it. `RenderFlags.SHIPPED` is the built-in
 * provider (the shipped default, diagnostics off). A host that lets an
 * operator flip them builds a `RenderFlagControls` (declared on `@tento/lona/host`),
 * passes it in its registries, and drives `setOverride` / `setDiagnostics`
 * from its persisted feature flags.
 */
export namespace RenderFlags {
  /**
   * THE production switch for the SDK 2.0 render pipeline.
   *
   * `false` = every slice rides dark: the scene is still built, diffed,
   * compiled, and refereed, but the positional bundle feeds `applyStructure`,
   * every cell rebinds, and no LR6 decision short-circuits a bind.
   * `true` = all three slices feed the live render path.
   *
   * Flip this one line to promote or demote the pipeline (behind an operator
   * gate + a live ux-test). Not env-driven — source only, so a bundle's
   * behavior is fully determined by the code it was built from.
   *
   * DEMOTED 2026-08-07: live-on drops the optimistic paint of a freshly created
   * event. Measured against a real account (10 calendars, events rendering
   * normally): creating through the create form persists the event and it
   * appears after a reload, but nothing paints at the moment of the create —
   * with the create form still open, so the user sees a click that did nothing.
   * Same create, same account, same event, pinning `render-next` off makes the
   * card paint live; pinning it on reproduces the miss (4 runs, deterministic).
   *
   * The failing slice is not identified here — the LR6 skip is the prime
   * suspect, since the optimistic path writes `canonicalCells` for a source row
   * that is NOT attached to the view (`EventsTable.writeCanonicalForKey`, the
   * Move-3 create-live fix), and a version probe that never observes that write
   * would authorize `shouldSkip` and short-circuit the repaint. Confirming that
   * belongs with the SDK 2.0 workstream; this line only stops shipping the
   * regression while that happens. Re-promote once a create renders live.
   */
  const SDK20_LIVE = false;

  /**
   * The shipped default, for a host flag to seed its own initial value from.
   * Keeps `SDK20_LIVE` the single place the pipeline's default is written.
   */
  export function defaultLive(): boolean {
    return SDK20_LIVE;
  }

  /**
   * The built-in provider (`SheetRegistries.BUILTINS.render`): the pipeline
   * at its shipped default, diagnostics off. Frozen.
   */
  export const SHIPPED: RenderFlags = Object.freeze({
    live: () => SDK20_LIVE,
    diagnostics: () => false,
  });
}
